/**
 * SupportTicketModal — F-013.
 *
 * Form ticket di supporto (UC-020): categoria, oggetto (max 120), messaggio
 * (max 2000), allegato opzionale (PDF/immagine, max 5MB), contesto pratica
 * auto-popolato se `praticaId` è passato dal chiamante (detail view).
 *
 * Su successo mostra il thank-you inline (reference copiabile). Su rate
 * limit (429 `SUPPORT_TICKET_RATE_LIMITED`) mostra lo stato "limite
 * raggiunto" e disabilita l'invio; lo stato è persistito in
 * `sessionStorage` per restare disabilitato se l'utente riapre il modal
 * entro l'ora (nessun endpoint GET dedicato in V1 — vedi Technical Notes).
 */
import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import {
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_OGGETTO_MAX,
  SUPPORT_TICKET_MESSAGGIO_MAX,
  SUPPORT_TICKET_ATTACHMENT_MAX_BYTES,
  SUPPORT_TICKET_ATTACHMENT_CONTENT_TYPES,
  type SupportTicketCategoria,
  type SupportTicketResponse,
} from '@app/shared';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { useCreateSupportTicket } from '../hooks/useCreateSupportTicket';
import { errorMessageFor, isSupportApiError } from '../lib/support-client';

const CATEGORIA_LABEL: Record<SupportTicketCategoria, string> = {
  bug: 'Bug',
  domanda: 'Domanda',
  pratica: 'Problema con una pratica',
  commerciale: 'Richiesta commerciale',
  altro: 'Altro',
};

const RATE_LIMIT_STORAGE_KEY = 'support-ticket-rate-limited-until';
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000;

function readRateLimitedUntil(): number | null {
  const raw = sessionStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  if (!raw) return null;
  const until = Number(raw);
  if (!Number.isFinite(until) || until <= Date.now()) return null;
  return until;
}

interface SupportTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Riferimento pratica auto-popolato se aperto da una detail view. */
  praticaId?: string;
}

export function SupportTicketModal({ open, onOpenChange, praticaId }: SupportTicketModalProps) {
  const [categoria, setCategoria] = useState<SupportTicketCategoria>('domanda');
  const [oggetto, setOggetto] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [allegato, setAllegato] = useState<File | null>(null);
  const [allegatoError, setAllegatoError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<SupportTicketResponse | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const { mutate, isPending, error } = useCreateSupportTicket();

  useEffect(() => {
    if (open) setRateLimitedUntil(readRateLimitedUntil());
  }, [open]);

  const isRateLimited = rateLimitedUntil !== null;
  const oggettoValid = oggetto.trim().length > 0 && oggetto.length <= SUPPORT_TICKET_OGGETTO_MAX;
  const messaggioValid =
    messaggio.trim().length > 0 && messaggio.length <= SUPPORT_TICKET_MESSAGGIO_MAX;
  const canSubmit = oggettoValid && messaggioValid && !isPending && !isRateLimited;

  function resetForm() {
    setCategoria('domanda');
    setOggetto('');
    setMessaggio('');
    setAllegato(null);
    setAllegatoError(null);
    setTicket(null);
    setCopied(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetForm();
    onOpenChange(next);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setAllegatoError(null);
    if (!file) {
      setAllegato(null);
      return;
    }
    const allowedTypes: readonly string[] = SUPPORT_TICKET_ATTACHMENT_CONTENT_TYPES;
    if (!allowedTypes.includes(file.type)) {
      setAllegatoError('Formato non ammesso. Usa PDF o immagine (PNG/JPEG/WebP).');
      e.target.value = '';
      setAllegato(null);
      return;
    }
    if (file.size > SUPPORT_TICKET_ATTACHMENT_MAX_BYTES) {
      setAllegatoError('Il file supera i 5MB.');
      e.target.value = '';
      setAllegato(null);
      return;
    }
    setAllegato(file);
  }

  function handleSubmit() {
    if (!canSubmit) return;
    mutate(
      {
        categoria,
        oggetto: oggetto.trim(),
        messaggio: messaggio.trim(),
        praticaId,
        allegato: allegato ?? undefined,
      },
      {
        onSuccess: (data) => setTicket(data),
        onError: (err) => {
          if (isSupportApiError(err) && err.code === 'SUPPORT_TICKET_RATE_LIMITED') {
            const until = Date.now() + RATE_LIMIT_WINDOW_MS;
            sessionStorage.setItem(RATE_LIMIT_STORAGE_KEY, String(until));
            setRateLimitedUntil(until);
          }
        },
      },
    );
  }

  async function handleCopyReference() {
    if (!ticket) return;
    await navigator.clipboard.writeText(ticket.referenceNumber);
    setCopied(true);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {ticket ? (
          <>
            <DialogHeader>
              <DialogTitle>Ticket inviato</DialogTitle>
              <DialogDescription>
                Riceverai una risposta entro qualche giorno lavorativo. Conserva il numero di
                riferimento per eventuali comunicazioni successive.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-between rounded-md border border-input bg-muted/40 px-3 py-2">
              <span className="font-mono text-sm font-semibold">{ticket.referenceNumber}</span>
              <Button type="button" variant="outline" size="sm" onClick={handleCopyReference}>
                {copied ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden="true" />
                )}
                <span className="ml-1.5">{copied ? 'Copiato' : 'Copia'}</span>
              </Button>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button">Chiudi</Button>
              </DialogClose>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Contatta il supporto</DialogTitle>
              <DialogDescription>
                Descrivi il tuo problema o la tua domanda: il nostro team ti risponderà entro
                qualche giorno lavorativo.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {isRateLimited ? (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  Limite raggiunto: hai aperto 5 ticket nell'ultima ora. Riprova più tardi.
                </p>
              ) : null}

              {praticaId ? (
                <p className="text-xs text-muted-foreground">
                  Pratica di riferimento: <span className="font-mono">{praticaId}</span>
                </p>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="support-categoria">Categoria</Label>
                <Select
                  id="support-categoria"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value as SupportTicketCategoria)}
                >
                  {SUPPORT_TICKET_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORIA_LABEL[c]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="support-oggetto">Oggetto</Label>
                <Input
                  id="support-oggetto"
                  value={oggetto}
                  onChange={(e) => setOggetto(e.target.value)}
                  maxLength={SUPPORT_TICKET_OGGETTO_MAX}
                  placeholder="es. Problema con l'upload del PDF"
                />
                <p className="text-right text-xs text-muted-foreground">
                  {oggetto.length}/{SUPPORT_TICKET_OGGETTO_MAX}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="support-messaggio">Messaggio</Label>
                <Textarea
                  id="support-messaggio"
                  rows={5}
                  value={messaggio}
                  onChange={(e) => setMessaggio(e.target.value)}
                  maxLength={SUPPORT_TICKET_MESSAGGIO_MAX}
                  placeholder="Descrivi il problema…"
                />
                <p className="text-right text-xs text-muted-foreground">
                  {messaggio.length}/{SUPPORT_TICKET_MESSAGGIO_MAX}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="support-allegato">Allegato (opzionale, PDF o immagine, max 5MB)</Label>
                <Input
                  id="support-allegato"
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={handleFileChange}
                />
                {allegatoError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {allegatoError}
                  </p>
                ) : null}
              </div>

              {error && !isRateLimited ? (
                <p role="alert" className="text-sm text-destructive">
                  {errorMessageFor(error)}
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isPending}>
                  Annulla
                </Button>
              </DialogClose>
              <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {isPending ? 'Invio in corso…' : isRateLimited ? 'Limite raggiunto' : 'Invia ticket'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
